import { useState } from 'react';
import { reviews } from '@/mocks/reviews';

interface ReviewsSectionProps {
  productId: string;
  rating: number;
  reviewCount: number;
}

function StarRow({ rating, count, total }: { rating: number; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="font-outfit text-xs text-gray-500 w-3 shrink-0">{rating}</span>
      <div className="w-4 h-4 flex items-center justify-center shrink-0">
        <i className="ri-star-fill text-[#C4FF61] text-xs"></i>
      </div>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#C4FF61] rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        ></div>
      </div>
      <span className="font-outfit text-xs text-gray-400 w-6 text-right shrink-0">{count}</span>
    </div>
  );
}

function ReviewCard({ review }: { review: (typeof reviews)[0] }) {
  const [helpful, setHelpful] = useState(review.helpful);
  const [voted, setVoted] = useState(false);

  return (
    <div className="border border-gray-100 rounded-2xl p-6 bg-white hover:border-gray-200 transition-colors">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <img
            src={review.avatar}
            alt={review.author}
            className="w-10 h-10 rounded-full object-cover object-top shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-outfit font-semibold text-[#1A1A1A] text-sm">{review.author}</span>
              {review.verified && (
                <span className="flex items-center gap-1 bg-[#C4FF61]/15 text-[#5a7a00] font-outfit font-semibold text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-shield-check-line text-[10px]"></i>
                  Verified Purchase
                </span>
              )}
            </div>
            <span className="font-inter text-xs text-gray-400">{review.date}</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {[1, 2, 3, 4, 5].map((s) => (
            <i
              key={s}
              className={`text-sm ${s <= review.rating ? 'ri-star-fill text-[#C4FF61]' : 'ri-star-line text-gray-200'}`}
            ></i>
          ))}
        </div>
      </div>

      <h4 className="font-outfit font-bold text-[#1A1A1A] text-sm mb-2">{review.title}</h4>
      <p className="font-inter text-gray-600 text-sm leading-relaxed mb-4">{review.body}</p>

      {review.tags && review.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {review.tags.map((tag) => (
            <span
              key={tag}
              className="bg-[#F7F8F9] text-gray-500 font-outfit text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
        <span className="font-inter text-xs text-gray-400">Helpful?</span>
        <button
          onClick={() => { if (!voted) { setHelpful((h) => h + 1); setVoted(true); } }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-outfit transition-all cursor-pointer whitespace-nowrap ${
            voted
              ? 'bg-[#C4FF61]/20 text-[#5a7a00] font-semibold'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
          }`}
        >
          <i className={`${voted ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-xs`}></i>
          Yes ({helpful})
        </button>
      </div>
    </div>
  );
}

export default function ReviewsSection({ productId, rating, reviewCount }: ReviewsSectionProps) {
  const productReviews = reviews.filter((r) => r.productId === productId);
  const displayReviews = productReviews.length > 0 ? productReviews : reviews.slice(0, 5);
  const [showAll, setShowAll] = useState(false);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'helpful' | 'recent'>('helpful');

  const ratingCounts = [5, 4, 3, 2, 1].map((r) => ({
    rating: r,
    count: displayReviews.filter((rv) => rv.rating === r).length,
  }));

  const filtered = displayReviews
    .filter((r) => filterRating === null || r.rating === filterRating)
    .sort((a, b) => sortBy === 'helpful' ? b.helpful - a.helpful : 0);

  const visible = showAll ? filtered : filtered.slice(0, 3);

  return (
    <section className="py-16 px-6 md:px-12 bg-white">
      <div className="max-w-5xl">
        <h2 className="font-outfit font-black text-[#1A1A1A] text-2xl md:text-3xl mb-10">
          Customer Reviews
        </h2>

        {/* Summary Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10 pb-10 border-b border-gray-100">
          {/* Big Score */}
          <div className="flex flex-col items-center justify-center bg-[#1A1A1A] rounded-2xl p-8 text-center">
            <span className="font-outfit font-black text-[#C4FF61] text-6xl leading-none">{rating}</span>
            <div className="flex items-center gap-1 mt-2 mb-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <i
                  key={s}
                  className={`text-base ${s <= Math.floor(rating) ? 'ri-star-fill text-[#C4FF61]' : s - 0.5 <= rating ? 'ri-star-half-fill text-[#C4FF61]' : 'ri-star-line text-white/20'}`}
                ></i>
              ))}
            </div>
            <span className="font-inter text-white/40 text-xs">{reviewCount} reviews</span>
          </div>

          {/* Rating Breakdown */}
          <div className="flex flex-col justify-center gap-2.5 md:col-span-2">
            <p className="font-outfit font-semibold text-[#1A1A1A] text-sm mb-1">Rating Breakdown</p>
            {ratingCounts.map(({ rating: r, count }) => (
              <button
                key={r}
                onClick={() => setFilterRating(filterRating === r ? null : r)}
                className={`cursor-pointer rounded-lg px-2 py-1 transition-colors ${filterRating === r ? 'bg-[#C4FF61]/10' : 'hover:bg-gray-50'}`}
              >
                <StarRow rating={r} count={count} total={displayReviews.length} />
              </button>
            ))}
          </div>
        </div>

        {/* Filter + Sort Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-outfit text-sm text-gray-500">Filter:</span>
            {[5, 4, 3].map((r) => (
              <button
                key={r}
                onClick={() => setFilterRating(filterRating === r ? null : r)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-outfit transition-all cursor-pointer whitespace-nowrap ${
                  filterRating === r
                    ? 'bg-[#1A1A1A] text-[#C4FF61]'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="ri-star-fill text-[10px]"></i>
                {r} Stars
              </button>
            ))}
            {filterRating !== null && (
              <button
                onClick={() => setFilterRating(null)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-outfit bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-xs"></i>
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-outfit text-sm text-gray-500">Sort:</span>
            {(['helpful', 'recent'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-outfit transition-all cursor-pointer whitespace-nowrap capitalize ${
                  sortBy === s
                    ? 'bg-[#1A1A1A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s === 'helpful' ? 'Most Helpful' : 'Most Recent'}
              </button>
            ))}
          </div>
        </div>

        {/* Review Cards */}
        <div className="flex flex-col gap-4">
          {visible.length > 0 ? (
            visible.map((r) => <ReviewCard key={r.id} review={r} />)
          ) : (
            <div className="text-center py-12 text-gray-400 font-outfit">
              No reviews match this filter.
            </div>
          )}
        </div>

        {/* Show More */}
        {filtered.length > 3 && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 border border-gray-200 text-[#1A1A1A] font-outfit font-semibold text-sm px-8 py-3 rounded-full hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              {showAll ? (
                <><i className="ri-arrow-up-line"></i> Show Less</>
              ) : (
                <><i className="ri-arrow-down-line"></i> Show All {filtered.length} Reviews</>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
